import { caCheckAtom } from '@/atoms/caCheck';
import { rugWalletAtom } from '@/atoms/rugWallet';
import { tokenInfoAtom } from '@/atoms/tokenInfo';
import { xAtom } from '@/atoms/xAtom';
import { ANTIRUG_CA } from '@/constants/antirug';
import { useJupiterModal } from '@/hooks/useJupiterModal';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import { AlertTriangle, CheckCircle2, Info, MessageCircle, Users, Wallet } from 'lucide-react';
import { FaXTwitter } from 'react-icons/fa6';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export function Overview() {
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const checkCaResult = useAtomValue(caCheckAtom);
  const rugWalletData = useAtomValue(rugWalletAtom);
  const xInfo = useAtomValue(xAtom);
  const { openJupiterModal } = useJupiterModal(ANTIRUG_CA);

  const score = checkCaResult?.score;

  return (
    <Card className="h-full flex flex-col justify-start">
      <CardHeader>
        <CardTitle>Overview</CardTitle>
      </CardHeader>
      <CardContent className="flex items-start sm:items-center justify-between flex-row gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {tokenInfo?.image ? (
            <div className="inline-block relative min-w-12 min-h-12">
              <a
                href={`https://lens.google.com/uploadbyurl?url=${tokenInfo.image}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={tokenInfo.image}
                  alt={tokenInfo.name || 'Token'}
                  className="size-12 rounded-full"
                />
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d6/Google_Lens_Icon.svg/120px-Google_Lens_Icon.svg.png"
                  alt="Google Lens"
                  style={{
                    position: 'absolute',
                    bottom: '1px',
                    right: '1px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: 'white',
                    padding: '1px',
                  }}
                />
              </a>
            </div>
          ) : (
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          )}
          <div>
            <h3 className="text-lg text-white font-medium max-w-[144px]">
              {tokenInfo?.name || 'Loading...'}
            </h3>
            <p className="text-gray-400">${tokenInfo?.symbol || '...'}</p>
          </div>
        </div>
        <div className="text-left sm:text-right">
          {typeof score === 'number' ? (
            <>
              <div
                className={`text-[2.5rem] font-bold  leading-none ${
                  score > 80
                    ? 'text-green-500'
                    : score > 50
                      ? 'text-yellow-500'
                      : score > 30
                        ? 'text-[#F4A261]'
                        : 'text-red-500'
                }`}
              >
                {score.toFixed(2)}%
              </div>
              <div className="text-sm text-gray-400">AI Safety Score</div>
            </>
          ) : (
            <>
              <Skeleton className="h-[40px] w-[145px]" />
              <div className="text-sm text-gray-400">AI Safety Score</div>
            </>
          )}
        </div>
      </CardContent>

      {/* New Promotional Component */}
      {score !== undefined && (score < 40 || score > 80) && (
        <div className="px-6 mb-4">
          <div className="bg-gradient-to-br from-indigo-900/40 via-purple-900/40 to-blue-900/40 p-4 rounded-xl border border-indigo-800/30 shadow-lg">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-indigo-100">
                  {score < 30
                    ? 'Did we just save you $30? Invest it in $ANTIRUG!'
                    : 'Did you just find a gem? Invest some in $ANTIRUG'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center bg-black/30 backdrop-blur-sm rounded-lg px-3 py-2 cursor-pointer hover:bg-black/40 transition-colors border border-indigo-800/30"
                  onClick={() => navigator.clipboard.writeText(ANTIRUG_TOKEN)}
                >
                  <span className="text-sm font-mono text-indigo-200">ts3...pump</span>
                  <DocumentDuplicateIcon className="h-4 w-4 ml-2 text-indigo-400" />
                </div>
                <button
                  onClick={openJupiterModal}
                  className="flex items-center gap-2 px-4 py-2 bg-black/30 hover:bg-black/40 backdrop-blur-sm rounded-lg transition-all hover:scale-105 border border-indigo-800/30"
                >
                  <Wallet className="h-4 w-4 text-indigo-200" />
                  <span className="text-sm font-medium text-indigo-200">Get $ANTIRUG</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rugWalletData && rugWalletData.data.length > 0 && (
        <div className="px-6 mb-4">
          <Accordion type="single" collapsible>
            <AccordionItem className="border-none" value="rug-history">
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-2 text-yellow-500">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm">Creator has suspicious history</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 mt-2">
                  {[...rugWalletData.data]
                    .sort(
                      (a, b) => new Date(b.entry_time).getTime() - new Date(a.entry_time).getTime(),
                    )
                    .map((entry, index) => (
                      <a
                        key={index}
                        href={`https://solscan.io/token/${entry.ca}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex justify-between items-center p-2 rounded hover:bg-gray-800 transition-colors"
                      >
                        <span className="text-sm text-gray-400 font-mono">
                          {entry.ca.slice(0, 8)}...{entry.ca.slice(-8)}
                        </span>
                        <span className="text-sm text-gray-400">
                          {new Date(entry.entry_time).toLocaleDateString()}
                        </span>
                      </a>
                    ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}

      {xInfo && !xInfo.error && (
        <CardFooter className="justify-between w-full mt-auto">
          <TooltipProvider>
            <div className="space-x-4 flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center space-x-1">
                    <Info className="size-4 text-white" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    This X username was provided by the token creator. <br />
                    Please DYOR and verify this token actually owns the associated X account.
                  </p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger
                  asChild
                  className="cursor-pointer hover:text-gray-400 "
                  onClick={() => {
                    window.open(`https://twitter.com/${xInfo.username}`);
                  }}
                >
                  <FaXTwitter size={20} className="text-white transition-colors" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>View on Twitter</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="space-x-4 flex items-center">
              {xInfo?.tweet_count && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-1">
                      <MessageCircle className="h-4 w-4 text-white" />
                      <span className="text-sm text-gray-400 font-medium">
                        {xInfo.tweet_count.toLocaleString()}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total Tweets</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {xInfo?.followers_count && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-1">
                      <Users className="h-4 w-4 text-white" />
                      <span className="text-sm text-gray-400 font-medium">
                        {xInfo.followers_count.toLocaleString()}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Followers</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {xInfo.verified && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-600">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This account is verified</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </CardFooter>
      )}
    </Card>
  );
}
